<template>
  <div class="public-hearing-page">

    <!-- Carregando -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-success" style="width:3rem;height:3rem;" role="status"></div>
      <p class="mt-3 text-muted">Carregando informações do evento...</p>
    </div>

    <!-- Não encontrado -->
    <div v-else-if="!evento" class="d-flex justify-content-center align-items-center" style="min-height:80vh">
      <div class="card text-center" style="max-width:480px;width:100%">
        <div class="card-body py-5 px-4">
          <i class="fa fa-times-circle fa-3x text-danger mb-3 d-block"></i>
          <h4>Evento não encontrado</h4>
          <p class="text-muted">Este link não é válido ou o evento foi encerrado.</p>
        </div>
      </div>
    </div>

    <!-- Conteúdo -->
    <div v-else class="public-hearing-container mx-auto py-4 px-3">

      <!-- Cabeçalho do evento -->
      <div class="card mb-4 border-0 shadow-sm overflow-hidden">
        <div class="public-hearing-header text-white text-center py-4 px-3">
          <div class="mb-2">
            <i class="fa fa-tree fa-2x"></i>
          </div>
          <h3 class="mb-1 font-weight-bold">{{ evento.title }}</h3>
          <p class="mb-1 opacity-75">
            <i class="fa fa-calendar mr-1"></i>{{ formatarData(evento.event_date) }}
          </p>
          <p class="mb-0 opacity-75">
            <i class="fa fa-map-marker mr-1"></i>{{ evento.location }}
          </p>
        </div>
        <div v-if="evento.description" class="card-body border-top">
          <p class="text-muted mb-0" style="white-space:pre-line">{{ evento.description }}</p>
        </div>
        <div class="card-body border-top py-2">
          <div class="d-flex justify-content-around text-center">
            <div>
              <strong class="text-primary d-block">{{ evento.total_registrations || 0 }}</strong>
              <small class="text-muted">inscritos</small>
            </div>
            <div>
              <strong class="text-success d-block">{{ evento.confirmed || 0 }}</strong>
              <small class="text-muted">confirmados</small>
            </div>
            <div v-if="evento.max_capacity">
              <strong class="text-secondary d-block">{{ evento.max_capacity }}</strong>
              <small class="text-muted">vagas</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Seção: Inscrição -->
      <div v-if="evento.status === 'open'" class="card mb-4 shadow-sm">
        <div class="card-header bg-light">
          <strong><i class="fa fa-user-plus mr-2"></i>Fazer Inscrição</strong>
        </div>

        <div v-if="inscricaoFeita" class="card-body text-center py-5">
          <i class="fa fa-check-circle fa-3x text-success mb-3 d-block"></i>
          <h5>Inscrição realizada com sucesso!</h5>
          <p class="text-muted mb-0">Você está inscrito na audiência pública. Lembre-se de confirmar sua presença no dia do evento.</p>
        </div>

        <div v-else class="card-body">
          <div class="form-group">
            <label class="font-weight-bold">Nome Completo <span class="text-danger">*</span></label>
            <input
              v-model="inscricao.full_name"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': erros.full_name }"
              placeholder="Seu nome completo"
            />
            <div v-if="erros.full_name" class="invalid-feedback">{{ erros.full_name }}</div>
          </div>

          <div class="form-group">
            <label class="font-weight-bold">E-mail</label>
            <input
              v-model="inscricao.email"
              type="email"
              class="form-control"
              :class="{ 'is-invalid': erros.email }"
              placeholder="seu@email.com"
            />
            <div v-if="erros.email" class="invalid-feedback">{{ erros.email }}</div>
          </div>

          <div class="form-group">
            <label class="font-weight-bold">Telefone</label>
            <input
              v-model="inscricao.phone"
              type="tel"
              class="form-control"
              :class="{ 'is-invalid': erros.phone }"
              placeholder="(XX) XXXXX-XXXX"
              maxlength="15"
              @input="mascararTelefone"
            />
            <div v-if="erros.phone" class="invalid-feedback">{{ erros.phone }}</div>
            <small class="text-muted">Informe ao menos e-mail ou telefone.</small>
          </div>

          <!-- Bairro -->
          <div v-if="evento.neighborhood_options && evento.neighborhood_options.length > 1" class="form-group">
            <label class="font-weight-bold">Bairro onde mora <span class="text-danger">*</span></label>
            <select
              v-model="inscricao.neighborhood_id"
              class="form-control"
              :class="{ 'is-invalid': erros.neighborhood_id }"
            >
              <option value="">Selecione seu bairro...</option>
              <option v-for="opt in evento.neighborhood_options" :key="opt.id" :value="opt.id">
                {{ opt.text }}
              </option>
            </select>
            <div v-if="erros.neighborhood_id" class="invalid-feedback">{{ erros.neighborhood_id }}</div>
            <div v-if="inscricao.neighborhood_id === 'outro'" class="mt-2">
              <input
                v-model="inscricao.neighborhood_other"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': erros.neighborhood_other }"
                placeholder="Informe seu bairro"
              />
              <div v-if="erros.neighborhood_other" class="invalid-feedback">{{ erros.neighborhood_other }}</div>
            </div>
          </div>

          <!-- Representa instituição -->
          <div class="form-group">
            <label class="font-weight-bold d-block">Representa uma instituição? <span class="text-danger">*</span></label>
            <div class="d-flex" style="gap:1.5rem">
              <label class="mb-0" style="cursor:pointer;font-weight:normal">
                <input
                  type="radio"
                  :value="true"
                  v-model="inscricao.represents_organization"
                  class="mr-1"
                />
                Sim
              </label>
              <label class="mb-0" style="cursor:pointer;font-weight:normal">
                <input
                  type="radio"
                  :value="false"
                  v-model="inscricao.represents_organization"
                  class="mr-1"
                />
                Não
              </label>
            </div>
            <div v-if="erros.represents_organization" class="text-danger small mt-1">{{ erros.represents_organization }}</div>
            <div v-if="inscricao.represents_organization === true" class="mt-2">
              <input
                v-model="inscricao.organization_name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': erros.organization_name }"
                placeholder="Nome da instituição"
              />
              <div v-if="erros.organization_name" class="invalid-feedback">{{ erros.organization_name }}</div>
            </div>
          </div>

          <div v-if="erroInscricao" class="alert alert-danger py-2 small">
            <i class="fa fa-exclamation-circle mr-1"></i>{{ erroInscricao }}
          </div>

          <button
            class="btn btn-success btn-block"
            :disabled="enviandoInscricao"
            @click="enviarInscricao"
          >
            <span v-if="enviandoInscricao"><i class="fa fa-spinner fa-spin mr-1"></i>Enviando...</span>
            <span v-else><i class="fa fa-check mr-1"></i>Confirmar Inscrição</span>
          </button>
        </div>
      </div>

      <div v-else-if="evento.status !== 'open'" class="alert alert-warning mb-4">
        <i class="fa fa-lock mr-2"></i>
        <strong>Inscrições encerradas.</strong>
        As inscrições para este evento não estão mais abertas.
      </div>

      <!-- Link para confirmação de presença -->
      <div class="card shadow-sm mb-4 border-0" style="background:#f0f7f0">
        <div class="card-body text-center py-4">
          <i class="fa fa-id-card fa-2x text-success mb-2 d-block"></i>
          <h6 class="mb-1">Já se inscreveu?</h6>
          <p class="text-muted small mb-3">
            Confirme sua presença no dia do evento.
          </p>
          <router-link
            :to="'/audiencia-publica/' + $route.params.token + '/confirmacao'"
            class="btn btn-outline-success btn-sm"
          >
            <i class="fa fa-check-circle mr-1"></i> Confirmar presença
          </router-link>
        </div>
      </div>

      <!-- Rodapé -->
      <p class="text-center text-muted small mt-4">
        <i class="fa fa-leaf mr-1 text-success"></i>
        Plano Municipal de Arborização Urbana
      </p>

    </div>
  </div>
</template>

<script>
import PublicHearingService from "../../servico/PublicHearingService";

export default {
  name: "PublicHearingPublic",
  data() {
    return {
      loading: true,
      evento: null,

      // Inscrição
      inscricao: {
        full_name: "",
        email: "",
        phone: "",
        neighborhood_id: "",
        neighborhood_other: "",
        represents_organization: null,
        organization_name: "",
      },
      erros: {},
      erroInscricao: "",
      enviandoInscricao: false,
      inscricaoFeita: false,

    };
  },
  beforeMount() {
    this.carregarEvento();
  },
  methods: {
    carregarEvento() {
      const token = this.$route.params.token;
      PublicHearingService.GetPublicEvent(token)
        .then((r) => { this.evento = r.data; })
        .catch(() => { this.evento = null; })
        .finally(() => { this.loading = false; });
    },

    mascararTelefone(e) {
      let v = e.target.value.replace(/\D/g, "");
      if (v.length <= 10) {
        v = v.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
      } else {
        v = v.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
      }
      this.inscricao.phone = v.substring(0, 15);
    },

    validarInscricao() {
      this.erros = {};
      if (!this.inscricao.full_name.trim()) {
        this.erros.full_name = "Nome completo é obrigatório.";
      }
      if (!this.inscricao.email && !this.inscricao.phone) {
        this.erros.email = "Informe ao menos e-mail ou telefone.";
        this.erros.phone = "Informe ao menos e-mail ou telefone.";
      }
      const hasNeighborhoodOptions = this.evento && this.evento.neighborhood_options && this.evento.neighborhood_options.length > 1;
      if (hasNeighborhoodOptions) {
        if (!this.inscricao.neighborhood_id) {
          this.erros.neighborhood_id = "Informe seu bairro.";
        } else if (this.inscricao.neighborhood_id === "outro" && !this.inscricao.neighborhood_other.trim()) {
          this.erros.neighborhood_other = "Informe o nome do seu bairro.";
        }
      }
      if (this.inscricao.represents_organization === null) {
        this.erros.represents_organization = "Resposta obrigatória.";
      } else if (this.inscricao.represents_organization === true && !this.inscricao.organization_name.trim()) {
        this.erros.organization_name = "Informe o nome da instituição";
      }
      return Object.keys(this.erros).length === 0;
    },

    neighborhoodName() {
      if (!this.inscricao.neighborhood_id) return null;
      if (this.inscricao.neighborhood_id === "outro") return this.inscricao.neighborhood_other.trim() || null;
      const opt = (this.evento.neighborhood_options || []).find((o) => o.id === this.inscricao.neighborhood_id);
      return opt ? opt.text : null;
    },

    enviarInscricao() {
      if (!this.validarInscricao()) return;
      this.erroInscricao = "";
      this.enviandoInscricao = true;
      const token = this.$route.params.token;
      const payload = {
        full_name: this.inscricao.full_name.trim(),
        email: this.inscricao.email.trim() || null,
        phone: this.inscricao.phone.trim() || null,
        neighborhood_id: this.inscricao.neighborhood_id || null,
        neighborhood_name: this.neighborhoodName(),
        represents_organization: this.inscricao.represents_organization,
        organization_name: this.inscricao.represents_organization ? (this.inscricao.organization_name.trim() || null) : null,
      };
      PublicHearingService.Register(token, payload)
        .then(() => { this.inscricaoFeita = true; })
        .catch((e) => {
          this.erroInscricao = e?.response?.data?.detail || "Erro ao realizar inscrição. Tente novamente.";
        })
        .finally(() => { this.enviandoInscricao = false; });
    },

    formatarData(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleDateString("pt-BR", {
        weekday: "long", day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    }
  }
};
</script>

<style scoped>
.public-hearing-page {
  min-height: 100vh;
  background: #f5f5f5;
}
.public-hearing-container {
  max-width: 600px;
}
.public-hearing-header {
  background: linear-gradient(135deg, #1B5E20 0%, #2E7D32 60%, #388E3C 100%);
}
.public-hearing-header .opacity-75 {
  opacity: 0.85;
}
</style>
